import React, { useState, useEffect } from 'react';
import { 
  Mail, Eye, MousePointer, AlertTriangle, CheckCircle, XCircle,
  ChevronDown, ChevronUp, RefreshCw, Users, BarChart3, Clock,
  ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsletterLogs = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('opened');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/newsletter-campaigns`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      console.error('Error loading campaigns:', e);
      toast.error('Errore caricamento campagne');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignDetails = async (campaignId) => {
    if (selectedCampaign === campaignId) {
      setSelectedCampaign(null);
      setCampaignDetails(null);
      return;
    }
    
    setSelectedCampaign(campaignId);
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/api/newsletter-campaigns/${campaignId}/details`, { headers });
      setCampaignDetails(res.data);
      setActiveDetailTab('opened');
    } catch (e) {
      console.error('Error loading campaign details:', e);
      toast.error('Errore caricamento dettagli');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCard = ({ icon: Icon, label, value, color, percentage }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
          </div>
        </div>
        {percentage !== undefined && (
          <span className={`text-lg font-semibold ${
            percentage > 50 ? 'text-green-600' : percentage > 20 ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {percentage}%
          </span>
        )}
      </div>
    </div>
  );

  const RecipientList = ({ recipients, type }) => {
    if (!recipients || recipients.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>Nessun destinatario in questa categoria</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {recipients.map((r, idx) => (
          <div key={idx} className="py-3 px-4 hover:bg-gray-50 flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">{r.name || 'N/A'}</p>
              <p className="text-sm text-gray-500">{r.email}</p>
            </div>
            <div className="text-right text-sm">
              {type === 'opened' && r.opened_at && (
                <span className="text-green-600">{formatDate(r.opened_at)}</span>
              )}
              {type === 'clicked' && r.clicked_at && (
                <div>
                  <span className="text-blue-600">{formatDate(r.clicked_at)}</span>
                  {r.urls && r.urls.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {r.urls.length} link cliccati
                    </p>
                  )}
                </div>
              )}
              {type === 'bounced' && r.bounce_reason && (
                <span className="text-red-600 text-xs">{r.bounce_reason}</span>
              )}
              {type === 'failed' && r.error && (
                <span className="text-red-600 text-xs">{r.error}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2A9FCB]">Log Invii Newsletter</h2>
          <p className="text-sm text-gray-500">Monitora aperture, click e bounce delle tue campagne</p>
        </div>
        <button
          onClick={loadCampaigns}
          className="flex items-center gap-2 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#2590BA] transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Aggiorna
        </button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Mail className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Nessuna campagna newsletter inviata</p>
          <p className="text-sm text-gray-400 mt-1">I log appariranno qui dopo l'invio di una newsletter</p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div key={campaign._id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Campaign Header */}
              <button
                onClick={() => loadCampaignDetails(campaign._id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#E6F4FA] rounded-lg">
                    <Mail className="w-6 h-6 text-[#2A9FCB]" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-800">{campaign.campaign_name || campaign.subject || 'Campagna'}</h3>
                    <p className="text-sm text-gray-500">{formatDate(campaign.sent_at)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  {/* Quick Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{campaign.total_sent || 0}</span>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <Eye className="w-4 h-4" />
                      <span className="font-medium">{campaign.open_rate || 0}%</span>
                    </div>
                    <div className="flex items-center gap-1 text-blue-600">
                      <MousePointer className="w-4 h-4" />
                      <span className="font-medium">{campaign.click_rate || 0}%</span>
                    </div>
                    {campaign.total_bounced > 0 && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">{campaign.total_bounced}</span>
                      </div>
                    )}
                  </div>
                  
                  {selectedCampaign === campaign._id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Details */}
              {selectedCampaign === campaign._id && (
                <div className="border-t border-gray-200 px-6 py-4">
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-[#2A9FCB]" />
                    </div>
                  ) : campaignDetails ? (
                    <div className="space-y-4">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <StatCard
                          icon={Users}
                          label="Inviati"
                          value={campaignDetails.total_recipients}
                          color="bg-gray-500"
                        />
                        <StatCard
                          icon={Eye}
                          label="Aperti"
                          value={campaignDetails.opened?.count || 0}
                          color="bg-green-500"
                          percentage={campaignDetails.total_recipients > 0 
                            ? Math.round((campaignDetails.opened?.count || 0) / campaignDetails.total_recipients * 100)
                            : 0}
                        />
                        <StatCard
                          icon={MousePointer}
                          label="Click"
                          value={campaignDetails.clicked?.count || 0}
                          color="bg-blue-500"
                          percentage={campaignDetails.total_recipients > 0 
                            ? Math.round((campaignDetails.clicked?.count || 0) / campaignDetails.total_recipients * 100)
                            : 0}
                        />
                        <StatCard
                          icon={AlertTriangle}
                          label="Bounce"
                          value={campaignDetails.bounced?.count || 0}
                          color="bg-orange-500"
                        />
                        <StatCard
                          icon={XCircle}
                          label="Falliti"
                          value={campaignDetails.failed?.count || 0}
                          color="bg-red-500"
                        />
                      </div>

                      {/* Detail Tabs */}
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex gap-2 mb-4">
                          {[
                            { id: 'opened', label: 'Aperti', icon: Eye, color: 'green' },
                            { id: 'clicked', label: 'Click', icon: MousePointer, color: 'blue' },
                            { id: 'not_opened', label: 'Non aperti', icon: Clock, color: 'gray' },
                            { id: 'bounced', label: 'Bounce', icon: AlertTriangle, color: 'orange' },
                            { id: 'failed', label: 'Falliti', icon: XCircle, color: 'red' }
                          ].map(tab => (
                            <button
                              key={tab.id}
                              onClick={() => setActiveDetailTab(tab.id)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                activeDetailTab === tab.id
                                  ? `bg-${tab.color}-100 text-${tab.color}-700`
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              style={activeDetailTab === tab.id ? {
                                backgroundColor: tab.color === 'green' ? '#D1FAE5' :
                                                tab.color === 'blue' ? '#DBEAFE' :
                                                tab.color === 'orange' ? '#FED7AA' :
                                                tab.color === 'red' ? '#FEE2E2' : '#F3F4F6',
                                color: tab.color === 'green' ? '#065F46' :
                                       tab.color === 'blue' ? '#1E40AF' :
                                       tab.color === 'orange' ? '#9A3412' :
                                       tab.color === 'red' ? '#991B1B' : '#374151'
                              } : {}}
                            >
                              <tab.icon className="w-4 h-4" />
                              {tab.label}
                              <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/50">
                                {campaignDetails[tab.id]?.count || 0}
                              </span>
                            </button>
                          ))}
                        </div>

                        {/* Recipients List */}
                        <div className="bg-gray-50 rounded-lg">
                          <RecipientList 
                            recipients={campaignDetails[activeDetailTab]?.recipients || []}
                            type={activeDetailTab}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsletterLogs;
